import { useMemo } from "react";

// Hero visual for landhep.id: "every point is already scored" drawn as a picture
// rather than shown as a screenshot. A lattice of cells shaped like a city, each
// coloured by its score, one chosen point with its radius, and two cards that say
// what the product reads there. Entirely illustrative — no real city, no real data —
// and deterministic, so it looks the same on every load.

const COLS = 30, ROWS = 22, CELL = 16, GAP = 2;
const STEP = CELL + GAP;
const W = COLS * STEP, H = ROWS * STEP;
const PICK = { x: 17, y: 12 };  // the chosen cell — clear of both cards

// Low → high, on the dark hero ground: clay for weak, brand greens for strong.
const RAMP = ["#b4532a", "#cf7a3c", "#dcb05a", "#6fbf98", "#1d9160", "#b7e0cd"];

function cells() {
  const out = [];
  const cx = COLS / 2, cy = ROWS / 2;
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      // An organic "city" outline: an ellipse whose radius wobbles with the angle.
      // Radii leave room for the wobble, so the outline never hits the frame flat.
      const dx = (x - cx) / (COLS * 0.41), dy = (y - cy) / (ROWS * 0.39);
      const a = Math.atan2(dy, dx);
      const edge = 1 + 0.13 * Math.sin(3 * a + 0.6) + 0.08 * Math.cos(5 * a - 1.1);
      if (Math.hypot(dx, dy) > edge) continue;

      // Smooth pseudo-terrain plus a hotspot around the chosen cell.
      let s = 0.48
        + 0.22 * Math.sin(x * 0.34 + 1.3) * Math.cos(y * 0.31 - 0.4)
        + 0.16 * Math.sin((x + y) * 0.19 + 0.8)
        + 0.1 * Math.cos(x * 0.83 - y * 0.47);
      const d = Math.hypot(x - PICK.x, y - PICK.y);
      s += 0.42 * Math.exp(-(d * d) / 18);
      s = Math.max(0, Math.min(0.999, s));
      out.push({ x, y, s, i: Math.min(RAMP.length - 1, Math.floor(s * RAMP.length)) });
    }
  }
  return out;
}

function ScoreCard() {
  return (
    <div className="h-full rounded-2xl border border-white/10 bg-white p-4 text-[#171C1F] shadow-2xl">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-700">Skor lokasi</p>
      <p className="mt-1 text-3xl font-extrabold tabular-nums">84<span className="text-sm font-semibold text-slate-400">/100</span></p>
      <div className="mt-2 h-1.5 rounded-full bg-slate-100"><div className="h-full w-[84%] rounded-full bg-brand-500" /></div>
      <p className="mt-2 text-[11px] leading-snug text-slate-500">Sangat cocok untuk profil bisnis Anda</p>
    </div>
  );
}

function RadiusCard() {
  return (
    <div className="h-full rounded-2xl border border-white/10 bg-white p-4 text-[#171C1F] shadow-2xl">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-700">Dalam radius 1 km</p>
      <dl className="mt-2 grid grid-cols-3 gap-1 text-center sm:gap-2">
        {[["48,2 rb", "populasi"], ["3", "kompetitor"], ["112", "POI"]].map(([n, l]) => (
          <div key={l}>
            <dt className="whitespace-nowrap text-[13px] font-extrabold tabular-nums sm:text-base">{n}</dt>
            <dd className="text-[9px] text-slate-500 sm:text-[10px]">{l}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function HeroScoreViz() {
  const grid = useMemo(cells, []);
  const px = PICK.x * STEP + CELL / 2, py = PICK.y * STEP + CELL / 2;

  return (
    <figure className="relative mx-auto w-full max-w-[560px]" aria-label="Visualisasi: setiap sel di kota diberi skor, dan satu titik terpilih dibaca isinya">
      <svg viewBox={`-8 -8 ${W + 16} ${H + 16}`} className="block w-full" role="img" aria-hidden="true">
        {grid.map(({ x, y, i }) => (
          <rect key={`${x}-${y}`} x={x * STEP} y={y * STEP} width={CELL} height={CELL} rx={3}
            fill={RAMP[i]} className="lh-cell" style={{ animationDelay: `${(x + y) * 28}ms`, opacity: 0.62 + i * 0.07 }} />
        ))}

        {/* The chosen point: radius ring, pulse, and the cell itself. */}
        <circle cx={px} cy={py} r={STEP * 4.2} fill="rgba(153,206,183,0.08)" stroke="#99ceb7" strokeWidth="1.5" strokeDasharray="5 5" className="lh-fade" />
        <circle cx={px} cy={py} r={STEP * 1.2} fill="none" stroke="#e3eae8" strokeWidth="2" className="lh-pulse" style={{ transformOrigin: `${px}px ${py}px` }} />
        <rect x={PICK.x * STEP - 1} y={PICK.y * STEP - 1} width={CELL + 2} height={CELL + 2} rx={4} fill="#ffffff" className="lh-fade" />
        <g className="lh-fade" transform={`translate(${px - 11} ${py - 40})`}>
          <path d="M11 0C4.9 0 0 4.8 0 10.8 0 18.8 11 30 11 30s11-11.2 11-19.2C22 4.8 17.1 0 11 0z" fill="#1d9160" stroke="#fff" strokeWidth="2" />
          <circle cx="11" cy="10.8" r="4" fill="#fff" />
        </g>
      </svg>

      {/* What the product reads at that point: floating beside the picture from sm up,
          stacked under it on phones, where they would otherwise cover the lattice. */}
      <div className="lh-fade absolute right-0 top-0 hidden w-48 sm:block lg:-right-4" style={{ animationDelay: "900ms" }}><ScoreCard /></div>
      <div className="lh-fade absolute bottom-6 left-0 hidden w-56 sm:block lg:-left-4" style={{ animationDelay: "1200ms" }}><RadiusCard /></div>
      <div className="mt-4 grid grid-cols-[1fr_1.3fr] gap-3 sm:hidden"><ScoreCard /><RadiusCard /></div>

      <figcaption className="mt-3 flex items-center justify-end gap-2 text-[11px] text-brand-100/60">
        <span>Skor rendah</span>
        <span className="flex gap-0.5">{RAMP.map((c) => <span key={c} className="h-2 w-4 rounded-sm" style={{ background: c }} />)}</span>
        <span>tinggi</span>
      </figcaption>
    </figure>
  );
}
